// src/components/Department/Department.jsx
// Main component for Department Management page

import React, { useState, useEffect } from 'react';

import departmentService from '../../services/departmentService';

import DepartmentTable from './DepartmentTable';
import DepartmentModal from './DepartmentModal';
import EditDepartmentModal from './EditDepartmentModal';
import ConfirmDialog from "../common/ConfirmDialog"; 
import { showNotification } from '../../utils/notification';
import { PlusCircle } from 'lucide-react';

import '../Inventory/inventory.css'; // Use same CSS as inventory

/**
 * Department Component
 * Main page component that manages departments
 * Handles CRUD operations and filtering
 */
const Department = ({ toggleSidebar }) => {
  // ========== STATE MANAGEMENT ==========

  // All departments from the backend
  const [departments, setDepartments] = useState([]);

  // Filtered departments (after applying search/filters)
  const [filteredDepartments, setFilteredDepartments] = useState([]);

  // Loading state
  const [isLoading, setIsLoading] = useState(true);

  // Saving state (for add operations)
  const [isSaving, setIsSaving] = useState(false);

  // Modal state
  const [isModalOpen, setIsModalOpen] = useState(false);

  // Currently selected department for editing (null for add mode)
  const [selectedDepartment, setSelectedDepartment] = useState(null);

  // Filter values
  const [filters, setFilters] = useState({
    search: '',
  });

  // Pagination
  const [page, setPage] = useState(1);
  const [limit] = useState(10);
  const [hasNextPage, setHasNextPage] = useState(true);
  const [totalPages, setTotalPages] = useState(1);

  const [isEditModalOpen, setIsEditModalOpen] = useState(false);
  const [isSavingEdit, setIsSavingEdit] = useState(false);
//deleting
  const [isConfirmOpen, setIsConfirmOpen] = useState(false);
  const [departmentToDelete, setDepartmentToDelete] = useState(null);
  const [isDeleting, setIsDeleting] = useState(false);

  const handleDeleteClick = (department) => {
  setDepartmentToDelete(department);
  setIsConfirmOpen(true);
};

  // ========== LOAD DATA ON COMPONENT MOUNT ==========

  /**
   * Load all departments from the backend when component first renders
   * This runs once when the component mounts
   */
  useEffect(() => {
    loadDepartments();
  }, [page]); // Runs on mount AND when page changes

  /**
   * Apply filters whenever departments or filter values change
   */
  useEffect(() => {
    applyFilters();
  }, [departments, filters]); // Run whenever departments or filters change

  // ========== DATA LOADING ==========

  /**
   * Fetch all departments from the backend
   */
  const loadDepartments = async () => {
    try {
      setIsLoading(true);

      // Call the API service
      const data = await departmentService.getAll(page, limit);
      setHasNextPage(data.length === limit);
      
      // Estimate total pages (this is approximate since we don't have total count)
      if (data.length < limit) {
        setTotalPages(page);
      } else {
        setTotalPages(page + 1); // At least one more page
      }

      // Update state with fetched data
      setDepartments(data);

      // Show success message
      showNotification(`Loaded ${data.length} departments`, 'success');
    } catch (error) {
      console.error('Error loading departments:', error);
      showNotification(`Failed to load departments: ${error.message}`, 'error');

      // Set empty array on error
      setDepartments([])
    } finally {
      // Always stop loading, whether successful or not
      setIsLoading(false);
    }
  };

  // ========== FILTERING ==========

  /**
   * Apply current filters to the department list
   */
  const applyFilters = () => {
    const safeDepartments = Array.isArray(departments) ? departments : [];
    let filtered = [...safeDepartments];

    // Filter by search term (searches name)
    if (filters.search.trim()) {
      const searchLower = filters.search.toLowerCase();

      filtered = filtered.filter((dept) => {
        const name = dept.name?.toLowerCase() || '';
        const description = dept.description?.toLowerCase() || '';
        return name.includes(searchLower) || description.includes(searchLower);
      });
    }

    setFilteredDepartments(filtered);
  };

  /**
   * Handle filter input changes
   */
  const handleFilterChange = (e) => {
    const { name, value } = e.target;

    setFilters((prevFilters) => ({
      ...prevFilters,
      [name]: value,
    }));
  };

  /**
   * Clear all filters
   */
  const handleClearFilters = () => {
    setFilters({
      search: '',
    });
    showNotification('Filters cleared', 'info');
  };

  // ========== MODAL OPERATIONS ==========

  /**
   * Open modal for adding new department
   */
  const handleAddNew = () => {
    setSelectedDepartment(null); // Clear any selected department
    setIsModalOpen(true);
  };

  /**
   * Open modal for editing existing department
   */
  const handleEdit = (department) => {
    setSelectedDepartment(department);
    setIsEditModalOpen(true);
  };

  /**
   * Close the modal
   */
  const handleCloseModal = () => {
    setIsModalOpen(false);
    setIsEditModalOpen(false);
    setSelectedDepartment(null);
  };

  const handleCloseEditModal = () => {
    setIsEditModalOpen(false);
    setSelectedDepartment(null);
  };

  // ========== CRUD OPERATIONS ==========

  /**
   * Save departments (create list)
   * @param {Object} payload - The form data from the modal
   */
  const handleSave = async (payload) => {
    try {
      setIsSaving(true);

      await departmentService.create(payload);

      showNotification(
        `New departments added successfully!`,
        'success'
      );

      setPage(1); // reset page
      handleCloseModal(); // close once
      await loadDepartments();
    } catch (error) {
      console.error('Error saving departments:', error);
      showNotification(`Failed to save: ${error.message}`, 'error');
    } finally {
      setIsSaving(false);
    }
  };

  /**
   * Delete a department
   */
  const confirmDelete = async () => {
  if (!departmentToDelete) return;

  try {
    setIsDeleting(true);
    await departmentService.delete(departmentToDelete.id); // API call

    showNotification("Department deleted successfully", "success");

    await loadDepartments(); // refresh table
  } catch (error) {
    console.error("Delete failed:", error);
    showNotification(`Failed to delete: ${error.message}`, "error");
  } finally {
    setIsDeleting(false);
    setIsConfirmOpen(false);
    setDepartmentToDelete(null);
  }
};

const cancelDelete = () => {
  setIsConfirmOpen(false);
  setDepartmentToDelete(null);
};

  // update single department
  const handleSaveEdits = async (formData) => {
    try {
      setIsSavingEdit(true);

      const payload = {
        name: formData.name,
        isActive: formData.isActive,
      };

      await departmentService.update(selectedDepartment.id, payload);

      showNotification(
        `Department "${payload.name}" updated successfully!`,
        'success'
      );

      await loadDepartments(); // refresh table
      handleCloseEditModal(); // close modal
    } catch (error) {
      console.error('Edit failed:', error);
      showNotification(`Failed to update: ${error.message}`, 'error');
    } finally {
      setIsSavingEdit(false);
    }
  };

  // ========== PAGINATION HELPERS ==========
  const handlePageChange = (newPage) => {
    setPage(newPage);
  };

  // ========== RENDER ==========

  return (
    <main className="inventory-main-content">
      {/* Page Header */}
      <header className="inventory-content-header">
        <button
          onClick={toggleSidebar}
          className="sidebar-toggle-btn"
          type="button"
        >
          ☰
        </button>
        <h2 className="inventory-content-title">Department Management</h2>

        <div className="header-actions">
          <button
            className="inventory-btn inventory-btn-add"
            onClick={handleAddNew}
            type="button"
          >
            <PlusCircle size={18} />
            Add New Department
          </button>
        </div>
      </header>

      {/* Filter Controls */}
      <section className="inventory-filter-controls">
        <div className="inventory-filter-group">
          {/* Search Input */}
          <input
            type="text"
            name="search"
            className="inventory-custom-input inventory-search-input"
            placeholder="Search Department Name or Description"
            value={filters.search}
            onChange={handleFilterChange}
          />

          {/* Apply Filter Button (filters apply automatically, but kept for UI consistency) */}
          {/* <button
            className="inventory-btn inventory-btn-primary"
            onClick={applyFilters}
            type="button"
          >
            Apply Filter
          </button> */}

          {/* Clear Filters Button */}
          <button
            className="inventory-btn inventory-btn-clear"
            onClick={handleClearFilters}
           
          >
            Clear Filters
          </button>
        </div>
      </section>

      {/* Data Table */}
      <section>
        <DepartmentTable
          departments={filteredDepartments}
          onEdit={handleEdit}
          onDelete={handleDeleteClick}
          isLoading={isLoading}
        />
      </section>

      {/* Pagination Controls */}
      <div className="pagination-controls">
        <button
          className="btn-page"
          disabled={page === 1}
          onClick={() => setPage((prev) => prev - 1)}
          type="button"
        >
          Previous
        </button>
        
        <div className="page-numbers">
          {page > 2 && (
            <>
              <button className="btn-page-num" onClick={() => handlePageChange(1)}>1</button>
              {page > 3 && <span className="page-ellipsis">...</span>}
            </>
          )}
          
          {page > 1 && (
            <button 
              className="btn-page-num" 
              onClick={() => handlePageChange(page - 1)}
            >
              {page - 1}
            </button>
          )}
          
          <button className="btn-page-num active">{page}</button>
          
          {hasNextPage && (
            <button 
              className="btn-page-num" 
              onClick={() => handlePageChange(page + 1)}
            >
              {page + 1}
            </button>
          )}
          
          {hasNextPage && page < totalPages - 1 && (
            <>
              {page < totalPages - 2 && <span className="page-ellipsis">...</span>}
              <button className="btn-page-num" onClick={() => handlePageChange(totalPages)}>{totalPages}</button>
            </>
          )}
        </div>

        <span className="page-info">Page {page} of {totalPages || 1}</span>

        <button
          className="btn-page"
          disabled={!hasNextPage}
          onClick={() => setPage((prev) => prev + 1)}
          type="button"
        >
          Next
        </button>
      </div>

      {/* Add Modal */}
      <DepartmentModal
        isOpen={isModalOpen}
        onClose={handleCloseModal}
        onSave={handleSave}
        department={selectedDepartment}
        isSaving={isSaving}
      />

      {/* Edit Modal */}
      <EditDepartmentModal
        isOpen={isEditModalOpen}
        onClose={handleCloseEditModal}
        onSave={handleSaveEdits}
        department={selectedDepartment}
        isSaving={isSavingEdit}
      />
      <ConfirmDialog
        isOpen={isConfirmOpen}
        title="Delete Department"
        message={`Are you sure you want to delete "${departmentToDelete?.name}"? This action cannot be undone.`}
        onConfirm={confirmDelete}
        onCancel={cancelDelete}
        confirmText="Delete"
        cancelText="Cancel"
        isLoading={isDeleting}
      />
    </main>
  );
};

export default Department;